import React, { useState } from 'react'
import { Star, Heart, Eye, Filter, Grid, List, TrendingUp, Users, Zap, Crown, ShoppingCart, Clock, Award } from 'lucide-react'

interface NFTMarketplaceProps {
  data: {
    marketplaceName: string
    description: string
    totalVolume: string
    totalSales: number
    totalUsers: number
    averagePrice: string
    featuredCollections: Array<{
      id: string
      name: string
      description: string
      floorPrice: string
      volume: string
      items: number
      owners: number
      verified: boolean
      image: string
    }>
    trendingNFTs: Array<{
      id: string
      name: string
      collection: string
      price: string
      lastSale: string
      image: string
      rarity: 'common' | 'rare' | 'epic' | 'legendary'
      creator: string
      verified: boolean
    }>
    categories: Array<{
      id: string
      name: string
      count: number
      icon: string
    }>
    creators: Array<{
      id: string
      name: string
      avatar: string
      verified: boolean
      totalSales: string
      items: number
      followers: number
      bio: string
    }>
    stats: {
      totalVolume24h: string
      totalSales24h: number
      activeUsers24h: number
      averagePrice24h: string
    }
  }
}

export default function NFTMarketplace({ data }: NFTMarketplaceProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [sortBy, setSortBy] = useState<string>('trending')
  const [favorites, setFavorites] = useState<Set<string>>(new Set())

  const toggleFavorite = (nftId: string) => {
    const newFavorites = new Set(favorites)
    if (newFavorites.has(nftId)) {
      newFavorites.delete(nftId)
    } else {
      newFavorites.add(nftId)
    }
    setFavorites(newFavorites)
  }

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'legendary': return 'bg-purple-500'
      case 'epic': return 'bg-blue-500'
      case 'rare': return 'bg-green-500'
      case 'common': return 'bg-gray-500'
      default: return 'bg-gray-500'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-pink-900 text-white">
      {/* Header */}
      <header className="bg-black/50 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                <Star className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 
                  className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"
                  style={{ fontFamily: 'Georgia, serif' }}
                >
                  {data.marketplaceName}
                </h1>
                <p className="text-purple-200 text-sm">NFT Marketplace</p>
              </div>
            </div>
            
            <nav className="hidden md:flex space-x-8">
              <a href="#explore" className="text-white/80 hover:text-white transition-colors">Explore</a>
              <a href="#collections" className="text-white/80 hover:text-white transition-colors">Collections</a>
              <a href="#creators" className="text-white/80 hover:text-white transition-colors">Creators</a>
              <a href="#activity" className="text-white/80 hover:text-white transition-colors">Activity</a>
            </nav>
            
            <div className="flex items-center space-x-4">
              <button className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors">
                Connect Wallet
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-4xl md:text-6xl font-bold mb-6"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Discover, Collect & Sell NFTs
            </h2>
            <p className="text-xl md:text-2xl text-purple-200 mb-8 max-w-3xl mx-auto">
              {data.description}
            </p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-8">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search collections, NFTs, or creators..."
                  className="w-full px-6 py-4 pl-12 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-purple-200 focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                />
                <Star className="absolute left-4 top-1/2 transform -translate-y-1/2 text-purple-400 w-5 h-5" />
              </div>
            </div>
          </div>

          {/* Marketplace Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="bg-gradient-to-r from-purple-500/20 to-purple-600/20 backdrop-blur-sm rounded-xl p-6 border border-purple-400/30">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-6 h-6 text-purple-400" />
                <span className="text-purple-400 font-semibold">Total Volume</span>
              </div>
              <div className="text-2xl font-bold text-white">{data.totalVolume}</div>
            </div>
            
            <div className="bg-gradient-to-r from-pink-500/20 to-pink-600/20 backdrop-blur-sm rounded-xl p-6 border border-pink-400/30">
              <div className="flex items-center gap-3 mb-2">
                <Star className="w-6 h-6 text-pink-400" />
                <span className="text-pink-400 font-semibold">Total Sales</span>
              </div>
              <div className="text-2xl font-bold text-white">{data.totalSales.toLocaleString()}</div>
            </div>
            
            <div className="bg-gradient-to-r from-blue-500/20 to-blue-600/20 backdrop-blur-sm rounded-xl p-6 border border-blue-400/30">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-6 h-6 text-blue-400" />
                <span className="text-blue-400 font-semibold">Total Users</span>
              </div>
              <div className="text-2xl font-bold text-white">{data.totalUsers.toLocaleString()}</div>
            </div>
            
            <div className="bg-gradient-to-r from-green-500/20 to-green-600/20 backdrop-blur-sm rounded-xl p-6 border border-green-400/30">
              <div className="flex items-center gap-3 mb-2">
                <Zap className="w-6 h-6 text-green-400" />
                <span className="text-green-400 font-semibold">Average Price</span>
              </div>
              <div className="text-2xl font-bold text-white">{data.averagePrice}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Collections */}
      <section className="py-16 px-6 bg-black/20">
        <div className="max-w-6xl mx-auto">
          <h2 
            className="text-3xl font-bold mb-8"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Featured Collections
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {data.featuredCollections.map((collection) => (
              <div
                key={collection.id}
                className="bg-white/10 backdrop-blur-sm rounded-xl overflow-hidden border border-white/20 hover:border-purple-400/50 transition-all duration-300 group"
              >
                <div className="aspect-video bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-purple-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Star className="w-8 h-8 text-purple-400" />
                    </div>
                    <p className="text-purple-300 text-sm">{collection.name}</p>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="text-xl font-bold text-white">{collection.name}</h3>
                    {collection.verified && (
                      <div className="bg-gradient-to-r from-gold-400 to-gold-600 rounded-full p-1">
                        <Star className="w-3 h-3 text-white fill-current" />
                      </div>
                    )}
                  </div>
                  
                  <p className="text-purple-200 mb-4 line-clamp-2">{collection.description}</p>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-sm text-purple-300">Floor Price</div>
                      <div className="font-bold text-white">{collection.floorPrice}</div>
                    </div>
                    <div>
                      <div className="text-sm text-purple-300">Volume</div>
                      <div className="font-bold text-white">{collection.volume}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm text-purple-300 mb-4">
                    <span>{collection.items.toLocaleString()} items</span>
                    <span>{collection.owners.toLocaleString()} owners</span>
                  </div>
                  
                  <button className="w-full bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-lg font-semibold transition-colors">
                    View Collection
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trending NFTs */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <h2 
              className="text-3xl font-bold"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Trending NFTs
            </h2>
            
            <div className="flex gap-4">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-purple-400"
              >
                <option value="trending">Trending</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="recent">Recently Listed</option>
              </select>
              
              <div className="flex bg-white/10 rounded-lg border border-white/20">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-purple-500' : 'hover:bg-white/10'} transition-colors`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-purple-500' : 'hover:bg-white/10'} transition-colors`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <div className={`grid gap-6 ${
            viewMode === 'grid' 
              ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
              : 'grid-cols-1'
          }`}>
            {data.trendingNFTs.map((nft) => (
              <div
                key={nft.id}
                className={`bg-white/10 backdrop-blur-sm rounded-xl overflow-hidden border border-white/20 hover:border-purple-400/50 transition-all duration-300 group ${
                  viewMode === 'list' ? 'flex' : ''
                }`}
              >
                <div className={`bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center relative ${
                  viewMode === 'list' ? 'w-32 h-32 flex-shrink-0' : 'aspect-square'
                }`}>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-purple-500/30 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Star className="w-8 h-8 text-purple-400" />
                    </div>
                    <p className="text-purple-300 text-sm">{nft.name}</p>
                  </div>
                  
                  {/* Rarity Badge */}
                  <div className="absolute top-2 left-2">
                    <span className={`px-2 py-1 rounded text-xs font-semibold text-white ${getRarityColor(nft.rarity)}`}>
                      {nft.rarity}
                    </span>
                  </div>
                  
                  {/* Favorite Button */}
                  <button
                    onClick={() => toggleFavorite(nft.id)}
                    className="absolute top-2 right-2 p-2 bg-black/50 rounded-full hover:bg-black/70 transition-colors"
                  >
                    <Heart 
                      className={`w-4 h-4 ${
                        favorites.has(nft.id) 
                          ? 'text-red-500 fill-current' 
                          : 'text-white'
                      }`} 
                    />
                  </button>
                </div>
                
                <div className={`p-4 ${viewMode === 'list' ? 'flex-1' : ''}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-white text-lg">{nft.name}</h3>
                    {nft.verified && (
                      <div className="bg-gradient-to-r from-gold-400 to-gold-600 rounded-full p-1">
                        <Star className="w-3 h-3 text-white fill-current" />
                      </div>
                    )}
                  </div>
                  
                  <p className="text-purple-300 text-sm mb-3">{nft.collection}</p>
                  
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="text-sm text-purple-300">Current Price</div>
                      <div className="text-lg font-bold text-white">{nft.price}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-purple-300">Last Sale</div>
                      <div className="text-white font-semibold">{nft.lastSale}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm text-purple-300 mb-4">
                    <span>by {nft.creator.slice(0, 6)}...{nft.creator.slice(-4)}</span>
                    <span className="flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      1.2K
                    </span>
                  </div>
                  
                  <div className="flex gap-2">
                    <button className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-2 px-3 rounded-lg text-sm font-semibold transition-colors">
                      Buy Now
                    </button>
                    <button className="bg-white/10 hover:bg-white/20 text-white py-2 px-3 rounded-lg transition-colors">
                      <ShoppingCart className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Top Creators */}
      <section className="py-16 px-6 bg-black/20">
        <div className="max-w-6xl mx-auto">
          <h2 
            className="text-3xl font-bold mb-8"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Top Creators
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.creators.map((creator, index) => (
              <div
                key={creator.id}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:border-purple-400/50 transition-all duration-300"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                      <Users className="w-8 h-8 text-white" />
                    </div>
                    {creator.verified && (
                      <div className="absolute -top-1 -right-1 bg-gradient-to-r from-gold-400 to-gold-600 rounded-full p-1">
                        <Star className="w-3 h-3 text-white fill-current" />
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-bold text-white">{creator.name}</h3>
                      <span className="bg-purple-500/20 text-purple-400 px-2 py-1 rounded text-xs font-semibold">
                        #{index + 1}
                      </span>
                    </div>
                    <p className="text-purple-300 text-sm">{creator.followers.toLocaleString()} followers</p>
                  </div>
                </div>
                
                <p className="text-white/80 text-sm mb-4 line-clamp-2">{creator.bio}</p>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <div className="text-sm text-purple-300">Total Sales</div>
                    <div className="font-bold text-white">{creator.totalSales}</div>
                  </div>
                  <div>
                    <div className="text-sm text-purple-300">Items</div>
                    <div className="font-bold text-white">{creator.items}</div>
                  </div>
                </div>
                
                <button className="w-full bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-lg font-semibold transition-colors">
                  Follow
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/50 backdrop-blur-sm border-t border-white/10 py-12 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex justify-center gap-6 mb-6">
            <a href="#" className="text-gray-400 hover:text-purple-400 transition-colors">Discord</a>
            <a href="#" className="text-gray-400 hover:text-purple-400 transition-colors">Twitter</a>
            <a href="#" className="text-gray-400 hover:text-purple-400 transition-colors">Instagram</a>
            <a href="#" className="text-gray-400 hover:text-purple-400 transition-colors">Blog</a>
          </div>
          <p className="text-gray-400">
            &copy; 2024 {data.marketplaceName}. The world's largest NFT marketplace.
          </p>
        </div>
      </footer>
    </div>
  )
}
